'use client'

import { useReturnOrderPrinter } from '@/lib/hooks'
import { cn, formatPrice } from '@/lib/utils'
import { Printer } from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'

import { Button, ScrollArea, Separator } from '@/components/ui'

export const PrintReturnReceipt = ({
  printerType,
  receipt,
  orderReturnId,
}: {
  printerType: string | null
  receipt: any
  orderReturnId: string
}) => {
  const { requestPrinter, printer } = useReturnOrderPrinter()
  const [isHtmlPrinting, startHtmlPrinting] = React.useTransition()

  const printNow = React.useCallback(async () => {
    startHtmlPrinting(async () => {
      try {
        const { getReturnOrderHtml } = await import('@/lib/actions')
        const html = await getReturnOrderHtml(orderReturnId)
        if (html?.url) {
          window.open(html?.url, '_blank')
        } else {
          toast.error('Failed to generate printable pdf')
        }
      } catch (error) {
        toast.error('Failed to generate printable pdf')
      }
    })
  }, [orderReturnId])

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-col gap-8">
        <ScrollArea className="max-h-[500px] print:h-auto">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1 text-sm">
                <p>Order ID: #{receipt?.order_return?.order_id}</p>
                <p>Returned on: {receipt?.order_return?.created_at}</p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-medium">Returned Items</h4>
              <div className="flex flex-col gap-2">
                {receipt?.order_return_items?.map(
                  (item: any, index: number) => (
                    <div
                      key={index}
                      className="flex flex-col gap-1 rounded border border-gray-100 p-2"
                    >
                      <div className="flex justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {item.order_item_id}
                          </span>
                          <span className="text-sm text-gray-600">
                            x{item.returned_qty}
                          </span>
                        </div>
                        <span className="font-medium">
                          Rs. {formatPrice(item.return_amount, 2, false)}
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="flex justify-between border-y border-gray-100 py-2 text-sm font-medium">
              <span>Total Return Amount</span>
              <span>
                Rs.{' '}
                {formatPrice(
                  receipt?.order_return?.total_return_amount,
                  2,
                  false,
                )}
              </span>
            </div>

            <div className="flex w-full flex-col gap-1 rounded-lg border border-gray-100 bg-gray-50 py-2">
              <h4 className="px-4 py-1 text-sm font-medium">Gift Card</h4>
              <Separator className="bg-gray-200" />
              <div className="flex flex-col gap-1 px-4 py-2">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-sm font-medium">Barcode</span>
                  <span className="text-sm font-medium text-blue-600">
                    {receipt?.['gift-card']?.barcode || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-sm font-medium">Amount</span>
                  <span className="text-sm font-medium">
                    Rs.{' '}
                    {formatPrice(Number(receipt?.['gift-card']?.amount), 2, false) ||
                      'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex w-full flex-col gap-2 print:hidden">
          {!printer && printerType === 'thermal' && (
            <Button size="lg" className="w-full" onClick={requestPrinter}>
              Connect Printer
            </Button>
          )}

          <div className="flex w-full items-center gap-2">
            {printerType !== 'thermal' && (
              <Button
                size="lg"
                className="w-full"
                onClick={printNow}
                disabled={isHtmlPrinting}
              >
                <Printer className="size-4" />
                {isHtmlPrinting ? 'Processing...' : 'Print Receipt'}
              </Button>
            )}

            {printer && printerType === 'thermal' && (
              <Button
                size="lg"
                className="w-full"
                onClick={printNow}
                disabled={!printer}
              >
                <Printer className="size-4" />
                Print Receipt
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
